import { TwoFAType } from '../../domain/two-factor';
import { TwoFactorService, TwoFAResult } from '../../services/two-factor.service';
import { TwoFactorMethodSetupCommonStep } from '../two-factor-method-setup-common/two-factor-method-setup-common.component';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { PPL_DIALOG_DATA, PplDialogRef } from '@ppl/ui/dialog';
import { TFormBuilder } from '@ppl/ui/form';
import { createDialogCloseResponse, createDialogSuccessResponse } from '@ppl/utils/dialog.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import { PplValidators } from '@ppl/utils/validators.utils';
import type {
  OnDestroy
} from '@angular/core';
import type { TFormGroup } from '@ppl/ui/form';
import type { DialogResponse } from '@ppl/utils/dialog.utils';
import type { Subscription } from 'rxjs';
import type { TwoFactorMethodSetupCommonFinishData, TwoFactorMethodSetupCommonInitData } from '../two-factor-method-setup-common/two-factor-method-setup-common.component';

@Component({
    selector: 'ppl-two-factor-method-setup-email',
    templateUrl: './two-factor-method-setup-email.component.html',
    styleUrls: ['./two-factor-method-setup-email.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TwoFactorMethodSetupEmailComponent implements OnDestroy {

  form: TFormGroup<TwoFactorMethodSetupEmailFormData>;

  step = TwoFactorMethodSetupCommonStep.INIT;

  TwoFAType = TwoFAType;

  statusPollingSubscription: Subscription;

  get actionDisabled() {
    return this.step === TwoFactorMethodSetupCommonStep.INIT
      ? !this.form.valid
      : false;
  }

  get securityToken() {
    return this.data.securityToken;
  }

  constructor(
    @Inject(PPL_DIALOG_DATA) private data: TwoFactorMethodSetupEmailData,
    private dialogRef: PplDialogRef<
      TwoFactorMethodSetupEmailComponent,
      DialogResponse<TwoFactorMethodSetupEmailResponse>
    >,
    private fb: TFormBuilder,
    private twoFactorService: TwoFactorService
  ) {
    this.form = this.fb.group<TwoFactorMethodSetupEmailFormData>({
      email: [this.data.email || '', [Validators.required, PplValidators.email]]
    });

    this.getSetupData = this.getSetupData.bind(this);
  }

  ngOnDestroy(): void {
    unsubscribe(this.statusPollingSubscription);
  }

  onNextStep(data: TwoFactorMethodSetupCommonInitData) {
    this.startStatusPolling(data.setupToken);
    this.step = TwoFactorMethodSetupCommonStep.VERIFY;
  }

  onCancel() {
    this.dialogRef.close(createDialogCloseResponse());
  }

  onFinish(data: TwoFactorMethodSetupCommonFinishData) {
    this.closeWithSuccess(data);
  }

  closeWithSuccess(data: { methodId: number; }) {
    this.dialogRef.close(createDialogSuccessResponse(data));
  }

  getSetupData() {
    const { email } = this.form.value;
    return {
      value: email
    };
  }

  startStatusPolling(setupToken: string) {
    unsubscribe(this.statusPollingSubscription);
    this.statusPollingSubscription = this.twoFactorService.pollStatus(
      this.twoFactorService.setupVerifyStatus({ type: TwoFAType.EMAIL, setupToken })
    ).subscribe({
      next: ([response, isDone]) => {
        if (isDone) {
          if (response.result === TwoFAResult.FINISHED) {
            this.closeWithSuccess({ methodId: response.id });
          }
        }
      }
    });
  }
}

export interface TwoFactorMethodSetupEmailResponse {
  methodId: number;
}

export interface TwoFactorMethodSetupEmailData {
  email: string;
  securityToken: string;
}

interface TwoFactorMethodSetupEmailFormData {
  email: string;
}
